import { useCallback, useEffect, useState } from 'react';
import { useEditor, useEditorState, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Link from '@tiptap/extension-link';
import History from '@tiptap/extension-history';
import { LinkIcon, Link2OffIcon } from 'lucide-react';
import sanitizeHtml from 'sanitize-html';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@design-system';

interface EmailSignatureEditorProps {
  id: string;
  label: string;
  description?: string;
  value: string;
  onChange: (html: string) => void;
  className?: string;
}

const SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: ['p', 'br', 'a'],
  allowedAttributes: {
    a: ['href', 'target', 'rel'],
  },
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  transformTags: {
    a: sanitizeHtml.simpleTransform('a', { target: '_blank', rel: 'noopener noreferrer' }, true),
  },
};

function sanitize(html: string): string {
  return sanitizeHtml(html, SANITIZE_OPTIONS);
}

function normalizeLinkUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return `mailto:${trimmed}`;
  return `https://${trimmed}`;
}

// Email signatures pasted from Gmail/Outlook often use a <div> per line;
// TipTap only recognizes <p> as a block, so div-lines would collapse into one
// paragraph. Rewriting them keeps the pasted line structure.
function transformPastedHtml(html: string): string {
  return html.replace(/<div\b/gi, '<p').replace(/<\/div>/gi, '</p>');
}

export function EmailSignatureEditor({
  id,
  label,
  description,
  value,
  onChange,
  className,
}: EmailSignatureEditorProps) {
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      HardBreak,
      History,
      Link.configure({
        openOnClick: false,
        autolink: false,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        id,
        class:
          'min-h-28 rounded-b-md bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none [&_p]:my-0 [&_a]:text-primary [&_a]:underline',
      },
      transformPastedHTML: transformPastedHtml,
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      const isEmpty = ed.getText().trim().length === 0;
      onChange(isEmpty ? '' : sanitize(html));
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    // Sanitizing normalizes markup (e.g. <br> vs <br />), so compare the
    // sanitized form too — otherwise the editor's own echoed value triggers
    // setContent on every keystroke and kicks the cursor to the end.
    if (incoming === current || incoming === sanitize(current)) return;
    if (incoming === '' && editor.getText().trim().length === 0) return;
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [editor, value]);

  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkUrlInput, setLinkUrlInput] = useState('');

  const { isLinkActive, currentHref, selectionEmpty } = useEditorState({
    editor,
    selector: ctx => ({
      isLinkActive: ctx.editor?.isActive('link') ?? false,
      currentHref: (ctx.editor?.getAttributes('link').href as string | undefined) ?? '',
      selectionEmpty: ctx.editor?.state.selection.empty ?? true,
    }),
  });

  const canEditLink = isLinkActive || !selectionEmpty;

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      setLinkPopoverOpen(open);
      if (open) {
        setLinkUrlInput(currentHref || 'https://');
      }
    },
    [currentHref],
  );

  const handleSaveLink = useCallback(() => {
    if (!editor) return;
    const href = normalizeLinkUrl(linkUrlInput);
    if (href) {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setLinkPopoverOpen(false);
  }, [editor, linkUrlInput]);

  const handleRemoveLink = useCallback(() => {
    editor?.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkPopoverOpen(false);
  }, [editor]);

  return (
    <div className={cn('mt-2', className)}>
      <label
        htmlFor={id}
        className="mb-1 block text-sm font-medium text-gray-950"
      >
        {label}
      </label>
      {description && <p className="mb-2 text-sm text-foreground">{description}</p>}
      <div className="rounded-md border overflow-hidden">
        <div className="flex items-center gap-1 border-b bg-gray-50 p-1">
          <Popover
            open={linkPopoverOpen}
            onOpenChange={handlePopoverOpenChange}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!canEditLink}
                title={canEditLink ? undefined : 'Select text to add a link'}
                className={cn(
                  'px-2 h-6 rounded py-1 text-xs font-medium',
                  {"bg-primary/7 text-primary": isLinkActive}
                )}
                aria-label={isLinkActive ? 'Edit link' : 'Add link'}
              >
                <LinkIcon className="size-3.5" />
                {isLinkActive ? 'Edit link' : 'Add link'}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-80 p-2"
            >
              <div className="flex flex-col gap-2">
                <Input
                  autoFocus
                  value={linkUrlInput}
                  onChange={e => setLinkUrlInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveLink();
                    }
                  }}
                  placeholder="https://example.com, name@email.com, or tel:…"
                  className="h-8 text-sm"
                />
                <div className="flex items-center justify-end gap-2">
                  {isLinkActive && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveLink}
                      className="h-7 px-2 text-xs font-medium text-red-600 hover:text-red-700"
                    >
                      <Link2OffIcon className="size-3.5" />
                      Remove
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveLink}
                    className="h-7 px-3 text-xs font-medium"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
